import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, useTheme, Avatar, Chip, Stack, Button, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '../../../theme';
import { SubTitleScreen, TitleScreen } from '../../../SystemDesign/Styles';
import LoaderSpinner from '../../../SystemDesign/LoaderSpinner';
import useDebounce from '../../../Utils/useDebounce';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { AuthContext } from '../../../Context/AuthContext';
import { useGetAuditLogsQuery } from '../../../services/SkillsManagement';
import ListViewV2 from '../../../SystemDesign/ListViewV2';
import DateInTable from '../../../SystemDesign/DateInTable';
import { formattedDate } from '../../../Utils/Functions';
import AuditToolbar from './AuditToolbar';
import AuditDetails from './AuditDetails';
import BulkDeleteDialog from './BulkDeleteDialog';
import AuditFiltersPanel from './AuditFiltersPanel';

const DEFAULT_ROWS_PER_PAGE_KEY = 'audit-rowsPerPage';

const getInitialRowsPerPage = () => {
  const saved = localStorage.getItem(DEFAULT_ROWS_PER_PAGE_KEY);
  return saved !== null ? parseInt(saved, 10) : 10;
};

const parseBool = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  if (v === 'true' || v === true) return true;
  if (v === 'false' || v === false) return false;
  return undefined;
};

const AuditLogsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const { ability } = useContext(AuthContext);

  // Responsive breakpoints
  const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const isSmOrLarger = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const limit = parseInt(searchParams.get('limit') || String(getInitialRowsPerPage()), 10);
    const sortBy = searchParams.get('sortBy') || 'created_at';
    const sortOrder = searchParams.get('sortOrder') || 'DESC';

    return {
      page,
      limit,
      sortBy,
      sortOrder,
      modelName: searchParams.get('modelName') || '',
      action: searchParams.get('action') || '',
      actorUserId: searchParams.get('actorUserId') ? Number(searchParams.get('actorUserId')) : undefined,
      entityId: searchParams.get('entityId') || '',
      module: searchParams.get('module') || '',
      success: parseBool(searchParams.get('success')),
      requestId: searchParams.get('requestId') || '',
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
      search: searchParams.get('search') || '',
    };
  });

  // Persist rows per page
  useEffect(() => {
    localStorage.setItem(DEFAULT_ROWS_PER_PAGE_KEY, String(query.limit));
  }, [query.limit]);

  // Search & filters (SystemDesign style)
  const [columnFilters, setColumnFilters] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 600);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // ListViewV2 query params cache (sorting/pagination for initial mount/URL-like state)
  const [queryParams, setQueryParams] = useState(() => ({
    filters: '',
    sorting: JSON.stringify({ column: query.sortBy || 'created_at', direction: (String(query.sortOrder || 'DESC').toLowerCase() === 'desc' ? 'desc' : 'asc') }),
    pagination: JSON.stringify({ currentPage: query.page || 1, rowsPerPage: query.limit || getInitialRowsPerPage() }),
    search: ''
  }));

  // Keep URL in sync with query state
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', String(query.page || 1));
    params.set('limit', String(query.limit || 10));
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortOrder) params.set('sortOrder', String(query.sortOrder));

    // Filters
    if (query.modelName) params.set('modelName', query.modelName);
    if (query.action) params.set('action', query.action);
    if (query.actorUserId !== undefined && query.actorUserId !== null && query.actorUserId !== '') params.set('actorUserId', String(query.actorUserId));
    if (query.entityId) params.set('entityId', query.entityId);
    if (query.module) params.set('module', query.module);
    if (typeof query.success === 'boolean') params.set('success', String(query.success));
    if (query.requestId) params.set('requestId', query.requestId);
    if (query.dateFrom) params.set('dateFrom', query.dateFrom);
    if (query.dateTo) params.set('dateTo', query.dateTo);
    if (query.search) params.set('search', query.search);

    const next = params.toString();
    const current = searchParams.toString();
    if (next !== current) {
      setSearchParams(params);
    }
  }, [query, setSearchParams]);

  // Server query args; use debounced search
  const queryArgs = useMemo(() => ({
    page: query.page,
    limit: query.limit,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
    action: query.action || undefined,
    success: typeof query.success === 'boolean' ? query.success : undefined,
    dateFrom: query.dateFrom || undefined,
    dateTo: query.dateTo || undefined,
    search: debouncedSearchValue || undefined,
  }), [query, debouncedSearchValue]);

  const { data, error, isFetching, isLoading, refetch } = useGetAuditLogsQuery(queryArgs, {
    refetchOnMountOrArgChange: false,
  });

  // Error handling via snackbar
  useEffect(() => {
    if (error) {
      const message = error?.data?.message || 'Une erreur est survenue lors du chargement des logs d\'audit';
      setInfoAlert({ severity: 'error', message });
      setOpenSnackBar(true);
    }
  }, [error, setInfoAlert, setOpenSnackBar]);

  // Selection & dialogs state
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLog, setDetailsLog] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const logs = data?.data || [];
  const pagination = data?.pagination || { total: 0, totalPages: 0, currentPage: query.page, limit: query.limit };

  const handleRowClick = (row) => {
    setDetailsLog(row);
    setDetailsOpen(true);
  };

  // Map ListViewV2 filters/pagination/sorting to API query state
  const applyFilters = (updatedFilters = columnFilters, paginationInfo = null, sortingInfo = null, searchVal = null) => {
    const filtersArray = updatedFilters || [];
    const formattedFilters = filtersArray.reduce((acc, filter) => {
      if (typeof filter === 'object' && !Array.isArray(filter)) {
        const [key, values] = Object.entries(filter)[0];
        if (Array.isArray(values)) {
          const nonEmptyValues = values.filter((val) => val !== '');
          if (nonEmptyValues.length > 0) {
            acc[key] = nonEmptyValues.map((val) => String(val));
          }
        } else if (values !== '') {
          acc[key] = [String(values)];
        }
      }
      return acc;
    }, {});

    // rowsPerPage from paginationInfo or localStorage
    let rowsPerPage = paginationInfo?.rowsPerPage;
    if (!rowsPerPage) {
      const saved = localStorage.getItem(DEFAULT_ROWS_PER_PAGE_KEY);
      rowsPerPage = saved !== null ? parseInt(saved, 10) : getInitialRowsPerPage();
    }

    // Update query only if something actually changed
    setQuery((prev) => {
      const next = {
        ...prev,
        page: paginationInfo?.currentPage || 1,
        limit: rowsPerPage,
        sortBy: sortingInfo?.column || prev.sortBy,
        sortOrder: sortingInfo?.direction ? String(sortingInfo.direction).toUpperCase() : prev.sortOrder,
        // Map filters to API params, preserving previous values when not provided
        modelName: formattedFilters['model_name'] ? formattedFilters['model_name'][0] : prev.modelName,
        action: formattedFilters['action'] ? formattedFilters['action'][0] : prev.action,
        module: formattedFilters['module'] ? formattedFilters['module'][0] : prev.module,
        success: formattedFilters['success']
          ? (formattedFilters['success'][0] === 'true' || formattedFilters['success'][0] === true)
          : prev.success,
        dateFrom: (formattedFilters['created_at'] && formattedFilters['created_at'][0])
          ? formattedFilters['created_at'][0]
          : prev.dateFrom,
        dateTo: (formattedFilters['created_at'] && formattedFilters['created_at'][1])
          ? formattedFilters['created_at'][1]
          : prev.dateTo,
        search: typeof searchVal === 'string' ? searchVal : prev.search,
      };
      const keys = ['page','limit','sortBy','sortOrder','modelName','action','module','success','dateFrom','dateTo','search'];
      const isEqual = keys.every((k) => prev[k] === next[k]);
      return isEqual ? prev : next;
    });

    // Keep ListViewV2 query params in sync (for UI state) only if changed
    setQueryParams((prev) => {
      const nextSorting = sortingInfo ? JSON.stringify(sortingInfo) : prev.sorting;
      const nextPagination = JSON.stringify({
        currentPage: paginationInfo?.currentPage || 1,
        rowsPerPage,
      });
      if (prev.sorting === nextSorting && prev.pagination === nextPagination) return prev;
      return {
        ...prev,
        sorting: nextSorting,
        pagination: nextPagination,
      };
    });
  };

  const handleExport = (scope = 'page') => {
    // Export to CSV; scope can be 'page' or 'all' (for now only current page to avoid heavy fetch)
    const rows = scope === 'page' ? logs : logs;
    if (!rows?.length) return;
    const header = ['id','user_id','username','email','role','action','model_name','module','success','route','ip_address','source_country','created_at'];
    const csv = [header.join(',')]
      .concat(
        rows.map(r => [
          r.id,
          r.user?.id ?? '',
          r.user?.username ?? '',
          r.user?.email ?? '',
          r.user?.role?.type ?? '',
          r.action ?? '',
          r.model_name ?? '',
          r.module ?? '',
          String(r.success),
          (r.route ?? '').replaceAll(',', ' '),
          r.ip_address ?? '',
          r.source_country ?? '',
          r.created_at,
        ].map(x => `"${String(x).replaceAll('"', '""')}"`).join(','))
      ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `audit-logs-${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openBulkDelete = () => setBulkDeleteOpen(true);
  const closeBulkDelete = () => setBulkDeleteOpen(false);

  const handleConfirmBulkDelete = async () => {
    // TODO: integrate with delete API when available.
    // For now, just close dialog and clear selection.
    setBulkDeleteOpen(false);
    setSelectedIds([]);
  };

  // Apply/Reset from collapsible AuditFiltersPanel
  const handleApplyFilters = (filters) => {
    // Collapsible panel no longer manages search/module/modelName
    setQuery((prev) => ({
      ...prev,
      action: filters.action || '',
      success: typeof filters.success === 'boolean' ? filters.success : undefined,
      dateFrom: filters.dateFrom || '',
      dateTo: filters.dateTo || '',
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setColumnFilters([]);
    // Clear search input UI
    setSearchValue('');
    setQuery((prev) => ({
      ...prev,
      modelName: '',
      action: '',
      module: '',
      success: undefined,
      dateFrom: '',
      dateTo: '',
      search: '',
      page: 1,
    }));
  };

  // Define columns before passing to ListViewV2
  const columns = useMemo(() => [
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'Date',
      enableColumnFilter: true,
      filterFn: 'between',
      type: 'date',
      cell: (info) => info.getValue() ? (
        <DateInTable date={formattedDate(new Date(info.getValue()))} />
      ) : '-',
    },
    {
      id: 'action',
      accessorKey: 'action',
      header: '',
      enableColumnFilter: true,
      cell: (info) => {
        const val = info.getValue();
        const color = val === 'CREATE' ? 'success' : val === 'UPDATE' ? 'warning' : val === 'DELETE' ? 'error' : 'default';
        return <Chip label={val || '-'} size="small" color={color} variant={color === 'default' ? 'outlined' : 'filled'} />
      },
    },
    {
      id: 'model_name',
      accessorKey: 'model_name',
      header: 'Model',
      enableColumnFilter: true,
      cell: (info) => info.getValue() || '-',
    },
    ...(isSmOrLarger ? [{
      id: 'module',
      accessorKey: 'module',
      header: 'Module',
      enableColumnFilter: true,
      cell: (info) => info.getValue() || '-',
    }] : []),
    ...(isSmOrLarger ? [{
      id: 'user',
      accessorKey: 'user',
      header: 'User',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original?.user;
        const name = user?.username || user?.email || 'Unknown';
        const email = user?.email || '';
        const role = user?.role?.type || '';
        const initials = (name || '').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
        const roleColor = ({
          EMPLOYEE: 'default',
          MANAGER: 'info',
          ADMIN: 'warning',
          SUPERADMIN: 'error',
          INSTRUCTOR: 'primary',
        }[role]) || 'default';
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>{initials || '?'}</Avatar>
            <Box>
              <Typography variant="body2" sx={{ lineHeight: 1.1 }}>{name}</Typography>
              {email && <Typography variant="caption" color="text.secondary">{email}</Typography>}
            </Box>
            {role && <Chip size="small" label={role} variant="outlined" color={roleColor} />}
          </Stack>
        );
      },
    }] : []),
    ...(isMdOrLarger ? [{
      id: 'success',
      accessorKey: 'success',
      header: 'Success',
      enableColumnFilter: true,
      enableSorting: false,
      cell: (info) => {
        const ok = Boolean(info.getValue());
        return <Chip label={ok ? 'Yes' : 'No'} size="small" color={ok ? 'success' : 'error'} variant="outlined" />
      },
    }] : []),
    ...(isMdOrLarger ? [{
      id: 'route',
      accessorKey: 'route',
      header: 'Route',
      enableSorting: false,
      enableColumnFilter: false,
      cell: (info) => (info.getValue() || '').toString(),
    }] : []),
    {
      id: 'ip_source',
      header: 'IP Source',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const ip = row.original?.ip_address || '-';
        const country = row.original?.source_country || '';
        const text = country ? `${ip} (${country})` : ip;
        return <Typography variant="body2" noWrap title={text}>{text}</Typography>;
      },
    },
    {
      id: 'diff',
      header: 'Changes',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const diff = row.original?.diff || {};
        const entries = Object.entries(diff);
        const count = entries.length;
        if (!count) return '-';
        const fmt = (v) => {
          if (v === null || v === undefined || v === '') return '–';
          if (typeof v === 'object') return '[object]';
          const s = String(v);
          return s.length > 30 ? `${s.slice(0, 27)}…` : s;
        };
        const summary = entries.map(([k, v]) => {
          const from = v && typeof v === 'object' ? v.from : undefined;
          const to = v && typeof v === 'object' ? v.to : undefined;
          return `${k}: ${fmt(from)} → ${fmt(to)}`;
        }).join('; ');
        return <Chip size="small" label={`${count} change${count > 1 ? 's' : ''}`} variant="outlined" color="info" title={summary} />;
      },
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Button size="small" onClick={() => handleRowClick(row.original)}>
          Details
        </Button>
      ),
    },
  ], [formattedDate, isMdOrLarger, isSmOrLarger]);

  return (
    <Box>
      <Typography sx={TitleScreen(colors)}>Audit Logs</Typography>
      <Typography sx={SubTitleScreen(colors)}>Track actions and events</Typography>

      <LoaderSpinner loading={isLoading} />

      <AuditToolbar
        selectedCount={selectedIds.length}
        total={pagination?.total || 0}
        page={query.page}
        limit={query.limit}
        onExport={handleExport}
        onBulkDelete={openBulkDelete}
        onRefresh={refetch}
      />

      <AuditFiltersPanel
        initialValues={query}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        isFetching={isFetching}
        colors={colors}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      <ListViewV2
        dispatchData={logs}
        columns={columns}
        tableName="audit"
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        applyFilters={applyFilters}
        pagination={{
          total: data?.pagination?.total || 0,
          totalPages: data?.pagination?.totalPages || 0,
          currentPage: data?.pagination?.currentPage || query.page,
          rowsPerPage: query.limit,
        }}
        setQueryParams={setQueryParams}
        queryParams={queryParams}
        debouncedSearchValue={debouncedSearchValue}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
      />

      <AuditDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        log={detailsLog}
      />

      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onCancel={closeBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        count={selectedIds.length}
      />
    </Box>
  );
};

export default AuditLogsPage;
